import React from 'react';
import { Box, Typography } from '@mui/material';
import { Appointment, AppointmentRecurring } from '@uplift/api';
import { useAppointmentCancellationDetailsQuery, useChangeSessionReasonByIdQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { UserRole } from '@uplift/types';
import { LoadableTypography } from '@uplift/ui-v2';


export type CancelledSessionDetailsProps = {
  appointment: Appointment;
};


const DetailsRow: React.FC<{ label: React.ReactNode }> = (props) => (
  <Box>
    <Typography variant="overline" color="secondary">
      {props.label}
    </Typography>
    <Typography>
      {props.children}
    </Typography>
  </Box>
);


const CancelledByLabel: React.VFC<{ cancelledByRole: UserRole; cancelledById?: string }> = ({ cancelledByRole, cancelledById }) => {
  const { data, isLoading } = useUserDisplayNameQuery(cancelledById, { enabled: cancelledByRole !== UserRole.superadmin });

  if (cancelledByRole === UserRole.superadmin) {
    return <>Admin Staff</>;
  }

  return (
    <LoadableTypography width="80px" loading={isLoading} component="span">
      {data?.fullLegalName}
    </LoadableTypography>
  );
};


export const CancelledAppointmentDetails: React.FC<CancelledSessionDetailsProps> = ({ appointment }) => {
  const { data: reason, isSuccess: isReasonLoaded } = useChangeSessionReasonByIdQuery(appointment.cancelReason ?? '', { enabled: !!appointment.cancelReason });
  const { data, isSuccess } = useAppointmentCancellationDetailsQuery(appointment.id);

  if (!isSuccess || !data) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <DetailsRow label="Cancelled by">
        <CancelledByLabel
          cancelledByRole={data.cancelledByRole}
          cancelledById={appointment.cancelledBy}
        />
      </DetailsRow>
      <DetailsRow label="Cancellation type">{{
        [AppointmentRecurring.none]: 'Appointment Canceled',
        [AppointmentRecurring.weekly]: 'Weekly Appointments Canceled',
        [AppointmentRecurring.biweekly]: 'Bi-Weekly Appointments Canceled',
        [AppointmentRecurring.monthly]: 'Monthly Appointments Canceled',
      }[data.cancellationType]}</DetailsRow>
      <DetailsRow label="Reason">
        {isReasonLoaded && reason
          ? (reason?.descriptionPastTense ?? reason?.description)
          : <Typography component="i" color="textDisabled">No reason selected</Typography>
        }
      </DetailsRow>
      <DetailsRow label="Notes">{appointment.cancellationNote
        ? appointment.cancellationNote
        : <Typography component="i" color="textDisabled">No additional notes shared</Typography>}
      </DetailsRow>
    </Box>
  );
};
